import React, { useEffect, useState, useRef } from 'react';
import Header from 'components/Header/Header';
import {
  faUser,
  faArrowLeft,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import Styles from './CriarUsuario.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector, useDispatch } from 'react-redux';
import { Creators as EstadosCreators } from 'store/ducks/estados';
import { Creators as GruposCreators } from 'store/ducks/grupos';
import { Creators as UsersCreators, Creators } from 'store/ducks/users';
import PropTypes from 'prop-types';
import SelectCheck from 'components/Template/SelectCheck/SelectCheck';
import { Select } from 'components/Template';
import { T, C } from 'shared/helpers';

const InputWithLabel = ({ label, ...props }) => (
  <div className={Styles.Container}>
    <p>{label}</p>
    <input className={Styles.Input} {...props} />
  </div>
);
InputWithLabel.propTypes = {
  label: PropTypes.any.isRequired,
};
const SelectWithLabel = ({ label, check = true, ...props }) => (
  <div className={Styles.Container}>
    <p>{label}</p>
    {check ? <SelectCheck {...props} /> : <Select {...props} />}
  </div>
);
SelectWithLabel.propTypes = {
  label: PropTypes.any.isRequired,
  ...SelectCheck.propTypes,
};

export default function CriarUsuario({
  match: {
    params: { id },
  },
  history,
}) {
  const [form, setForm] = useState({});
  /** @type {[User, React.Dispatch<User>]} */
  const [currUser, setCurrUser] = useState(undefined);
  const dispatch = useDispatch();
  const estados = useSelector(state => state.estados.data);
  const availableGrupos = useSelector(state => state.grupos.data);
  const availableUsers = useSelector(state => state.users.data);
  const loading = useSelector(state => state.users.loadingUserUpdate);
  const errors = useSelector(state => state.users.error);
  const prevAvailableUsers = useRef();

  useEffect(() => {
    if (loading === true) {
      prevAvailableUsers.current = [...availableUsers];
    } else if (
      loading === false &&
      errors === undefined &&
      prevAvailableUsers.current &&
      availableUsers
    ) {
      setCurrUser(undefined);
      if (prevAvailableUsers.current.length !== availableUsers.length) {
        history.replace('/users/' + availableUsers.slice(-1)[0].id);
      }
      window.scrollTo(0, 0);
    }
  }, [loading, availableUsers, prevAvailableUsers, history, errors]);

  useEffect(() => {
    dispatch(EstadosCreators.getEstados());
    dispatch(GruposCreators.getGrupos());
    dispatch(UsersCreators.getUsers());
  }, [dispatch]);

  useEffect(() => {
    if (!id) {
      setForm({});
    } else if (currUser) {
      setForm({
        ...currUser,
        roles: T(currUser.roles || [], []).map(r => r.id),
        parent_id: T(currUser.parents || [], []).map((/** @type {User} */ r) => r.id),
      });
    } else if (availableUsers) {
      setCurrUser(availableUsers.find(a => a.id === Number(id)));
    }
  }, [id, currUser, availableUsers]);

  function updateForm({ target: { name, value } }) {
    setForm({ ...form, [name]: value });
  }

  function submit() {
    dispatch(Creators.putUser(form));
    if (errors) {
      setModalOpen(true);
    }
  }

  const [modalOpen, setModalOpen] = useState(false);
  function close() {
    setModalOpen(false);
  }

  useEffect(() => {
    if (errors) {
      setModalOpen(true);
    } else {
      setModalOpen(false);
    }
  }, [errors]);

  return (
    <div className={Styles.Container}>
      <div className={C(Styles.Modal, [modalOpen, Styles.Open])}>
        <main>
          <header>
            <div id='user_removal_close_btn'>
              <FontAwesomeIcon icon={faTimes} onClick={close} />
            </div>
          </header>
          <ul>
            {errors ? errors.split('\n').map(e => <li>{e}</li>) : undefined}
          </ul>
          <button className={C(Styles.Button, Styles.Save)} onClick={close}>
            OK
          </button>
        </main>
      </div>
      <Header
        title={
          form && form.id ? 'Editar dados do usuário' : 'Criar um novo usuário'
        }
        icon={faUser}
      />
      <main className={Styles.Content}>
        <div className={Styles.Controls}>
          <button onClick={() => history.goBack()}>
            <FontAwesomeIcon icon={faArrowLeft} />
            &nbsp;Voltar
          </button>
        </div>
        <InputWithLabel
          data-testid='input-user-name'
          name='name'
          onChange={updateForm}
          value={form.name || ''}
          label='Nome'
        />
        <InputWithLabel
          name='email'
          onChange={updateForm}
          value={form.email || ''}
          label='Email'
        />
        <InputWithLabel
          name='password'
          onChange={updateForm}
          value={form.password || ''}
          type='password'
          label='Senha'
        />
        <InputWithLabel
          name='password_confirmation'
          onChange={updateForm}
          value={form.password_confirmation || ''}
          type='password'
          label='Repita  a senha'
        />
        <SelectWithLabel
          label='Grupos'
          items={availableGrupos}
          onChange={newSelecteds =>
            setForm({ ...form, roles: newSelecteds.map(r => String(r.id)) })
          }
          renderItem={i => T(i).name}
          filter={(filterText, item) =>
            item.name.toLowerCase().indexOf(filterText.toLowerCase()) !== -1
          }
          selectedItems={T(form.roles || [], []).map(id =>
            availableGrupos.find(u => Number(u.id) === Number(id)),
          ).filter(Boolean)}
        />
        <SelectWithLabel
          label='Pertence a'
          items={availableUsers}
          onChange={parentUsers =>
            setForm({ ...form, parent_id: parentUsers.map(u => String(u.id)) })
          }
          renderItem={i => T(i).name}
          filter={(filterText, item) =>
            item.name.toLowerCase().indexOf(filterText.toLowerCase()) !== -1
          }
          selectedItems={T(form.parent_id || [], []).map(id =>
            availableUsers.find(u => Number(u.id) === Number(id)),
          ).filter(Boolean)}
        />
        <InputWithLabel
          name='cpf'
          onChange={updateForm}
          value={form.cpf || ''}
          label='CPF'
        />
        <InputWithLabel
          name='endereco'
          onChange={updateForm}
          value={form.endereco || ''}
          label='Endereço'
        />
        <InputWithLabel
          name='numero'
          onChange={updateForm}
          value={form.numero || ''}
          label='Número'
        />
        <InputWithLabel
          name='complemento'
          onChange={updateForm}
          value={form.complemento || ''}
          label='Complemento'
        />
        <InputWithLabel
          name='bairro'
          onChange={updateForm}
          value={form.bairro || ''}
          label='Bairro'
        />
        <InputWithLabel
          name='cidade'
          onChange={updateForm}
          value={form.cidade || ''}
          label='Cidade'
        />
        <SelectWithLabel
          id='estados_selector'
          items={estados}
          onChange={estado => setForm({ ...form, estado: estado.nome })}
          label='Estado'
          check={false}
          valueRef={form.estado}
          setValueRef={nome => setForm({ ...form, estado: nome })}
          options={estados.map(e => ({ label: e.nome, value: e.nome }))}
        />
        <InputWithLabel
          name='profissao'
          onChange={updateForm}
          value={form.profissao || ''}
          label='Profissão'
        />
        <div className={Styles.Row}>
          <InputWithLabel
            name='ddd_telefone'
            onChange={updateForm}
            value={form.ddd_telefone || ''}
            label='DDD'
          />
          <InputWithLabel
            name='telefone'
            onChange={updateForm}
            value={form.telefone || ''}
            label='Telefone'
          />
        </div>
        <div className={Styles.Row}>
          <InputWithLabel
            name='ddd_celular'
            onChange={updateForm}
            value={form.ddd_celular || ''}
            label='DDD'
          />
          <InputWithLabel
            name='celular'
            onChange={updateForm}
            value={form.celular || ''}
            label='Celular'
          />
        </div>
        <br />
        <span>
          <b>Ativo</b>{' '}
          <input
            type='checkbox'
            onChange={({ target: { checked } }) => {
              setForm({ ...form, status: checked ? 1 : 0 });
            }}
            checked={form.status}
          />
        </span>
        <div>
          <br />
          <button
            className={C(Styles.Button, Styles.Save)}
            onClick={submit}
            id='btn_salvar_usuario'
          >
            Salvar
          </button>
        </div>
      </main>
    </div>
  );
}

CriarUsuario.propTypes = {
  match: PropTypes.any.isRequired,
  history: PropTypes.any.isRequired,
};
